import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { hivesOverviewRoute } from '@/features/HivesOverview/pages/HivesOverviewHivesTable'
import {
  type AcceptedResolution,
  ResolutionReceipt,
} from '@/features/SalesforceIngest/components/ResolutionReceipt'
import { ResolveIngestForm } from '@/features/SalesforceIngest/components/ResolveIngestForm'
import { useCanResolveSalesforceIngest } from '@/features/SalesforceIngest/hooks/useCanResolveSalesforceIngest'
import { salesforceAccountsRootRoute } from '@/router/rootRoutes'
import { Box, Skeleton, Text } from '@chakra-ui/react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * A static sibling of `$salesforceAccountId` under the same parent. TanStack
 * ranks static above dynamic so this wins the match, and `AccountLookupForm`
 * validates the 15/18-character pattern before navigating, so it can never
 * produce a detail URL that collides with this path.
 *
 * The route sits with the accounts screen because that screen is what
 * supplies the `orgId` this form asks for: look the Salesforce account up,
 * read off its organisations, resolve. One click apart rather than two
 * screens and a clipboard. The code lives in its own feature folder, so
 * APP-2430 can move the route without moving the code.
 */
export const salesforceIngestResolveRoute = createRoute({
  getParentRoute: () => salesforceAccountsRootRoute,
  path: 'resolve',
  component: ResolveIngestPage,
})

/**
 * Owns the one piece of state the two children do not: whether a resolution
 * has been accepted. That keeps the form and the receipt presentational and
 * makes the swap a single conditional.
 */
export function ResolveIngestPage() {
  const { t } = useTranslation('features', { useSuspense: false })
  const { canResolve, isResolved } = useCanResolveSalesforceIngest()
  const navigate = useNavigate()

  const [accepted, setAccepted] = useState<AcceptedResolution | null>(null)

  // `isResolved` first, always: the roles query is still pending on the first
  // render of a hard page load, so redirecting on `canResolve` alone bounces
  // every deep link before the viewer's roles have arrived.
  useEffect(() => {
    if (isResolved && !canResolve) {
      void navigate({ to: hivesOverviewRoute.to })
    }
  }, [canResolve, isResolved, navigate])

  // Both halves of the swap unmount the control that caused them (the submit
  // button on a 202, "Resolve another" on the way back), so focus would land
  // on `<body>` and a screen reader would say nothing at all. Moving it to
  // the region that replaced them announces what happened and leaves the
  // keyboard somewhere useful. The region is named, so it is announced as
  // "Resolution accepted" or "Stuck ingest" rather than as a bare group.
  const swappedRef = useRef<HTMLElement>(null)
  const wasAccepted = useRef<boolean | null>(null)
  const hasAccepted = accepted !== null
  useEffect(() => {
    if (wasAccepted.current !== null && wasAccepted.current !== hasAccepted) {
      swappedRef.current?.focus()
    }
    wasAccepted.current = hasAccepted
  }, [hasAccepted])

  if (isResolved && !canResolve) {
    return null
  }

  return (
    <PageLayout
      title={String(t('salesforceIngest.pageTitle'))}
      description={
        <Text color="text.text-subtle">
          {String(t('salesforceIngest.description'))}
        </Text>
      }
      maxWidth="8xl"
    >
      {isResolved ? (
        <Box
          as="section"
          ref={swappedRef}
          tabIndex={-1}
          aria-label={String(
            accepted
              ? t('salesforceIngest.receipt.title')
              : t('salesforceIngest.form.title'),
          )}
          outline="none"
        >
          {accepted ? (
            <ResolutionReceipt
              accepted={accepted}
              onResolveAnother={() => setAccepted(null)}
            />
          ) : (
            <ResolveIngestForm onAccepted={setAccepted} />
          )}
        </Box>
      ) : (
        <Skeleton h={80} w="100%" />
      )}
    </PageLayout>
  )
}
